import { useRef, type FC } from 'react';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { createClickTrackingEvent } from '../helpers/trackingHelpers.ts';
import { type DesignsFeaturedItems_iconicDesignsDirectoryPage$data as IconicDesignsDirectoryPage } from './__generated__/DesignsFeaturedItems_iconicDesignsDirectoryPage.graphql.ts';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';

// helpers
import {
    getSrcString,
    getSrcSetString,
    MOBILE_IMAGE_DEFAULT_SIZE,
    RESP_IMAGE_DEFAULT_SIZE,
} from '../../utils/srcStringHelpers.ts';

// styles
import styles from './DesignsFeaturedItems.scss';
import { Link } from 'dibs-elements/exports/Link.tsx';

type ImgProps = {
    src: string;
    offsetVertical: number;
    alt: string | undefined;
    className: string | undefined;
    sizes: string;
    defaultImageSize: number;
};

const LazyImage: FC<ImgProps> = ({
    src,
    alt,
    sizes,
    className,
    defaultImageSize,
    offsetVertical = 100,
}) => {
    const imageRef = useRef<HTMLImageElement>(null);

    return (
        <>
            <img
                alt={alt}
                sizes={sizes}
                ref={imageRef}
                className={className}
                src={'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='}
            />
            <VisibilityTracker
                elementRef={imageRef}
                onVisibilityChange={({ isVisible }: { isVisible: boolean }) => {
                    if (isVisible && imageRef && imageRef.current) {
                        imageRef.current.srcset = getSrcSetString({ url: src });
                        imageRef.current.src = getSrcString({
                            url: src,
                            width: defaultImageSize,
                        });
                    }
                }}
                observerOptions={{
                    rootMargin: `${offsetVertical}px 0px ${offsetVertical}px 0px`,
                }}
            />
        </>
    );
};

type Props = {
    isMobile: boolean;
    iconicDesignsDirectoryPage: IconicDesignsDirectoryPage;
};

const INITIAL_LOADED_IMAGES_MOBILE: number = 2;
const INITIAL_LOADED_IMAGES_DESKTOP: number = 4;

const VERTICAL_OFFSET_MOBILE: number = 200;
const VERTICAL_OFFSET_DESKTOP: number = 400;

const trackClick = createClickTrackingEvent('featured iconic design clicked');

const DesignsFeaturedItemsComponent: FC<Props> = ({ isMobile, iconicDesignsDirectoryPage }) => {
    const { designPages } = iconicDesignsDirectoryPage;
    const containerRef = useRef(null);
    const initialLoadedImages = isMobile
        ? INITIAL_LOADED_IMAGES_MOBILE
        : INITIAL_LOADED_IMAGES_DESKTOP;
    const defaultImageSize = isMobile ? MOBILE_IMAGE_DEFAULT_SIZE : RESP_IMAGE_DEFAULT_SIZE;

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    <Heading className={styles.header}>
                        <div data-tn="featured-designs-header">
                            <FormattedMessage
                                id="designs.designsFeaturedItems.header"
                                defaultMessage="Featured Designs"
                            />
                        </div>
                    </Heading>
                    <div
                        data-tn="featured-designs-container"
                        className={classnames({
                            [styles.container]: !isMobile,
                            [styles.mobileContainer]: isMobile,
                        })}
                        ref={containerRef}
                    >
                        {designPages?.map((page, index) => {
                            const { designName, designUri, imageUrl, creators } = page || {};

                            if (!designUri || !imageUrl) {
                                return null;
                            }

                            const imageElement = (
                                <img
                                    className={styles.img}
                                    alt={designName || undefined}
                                    srcSet={getSrcSetString({ url: imageUrl })}
                                    sizes="(max-width:768px) 100px, 200px"
                                    src={getSrcString({
                                        url: imageUrl,
                                        width: defaultImageSize,
                                    })}
                                />
                            );

                            return (
                                <div
                                    key={`${designUri}-${index}`}
                                    className={classnames({
                                        [styles.item]: !isMobile,
                                        [styles.mobileItem]: isMobile,
                                    })}
                                >
                                    <Link
                                        href={designUri}
                                        onClick={trackClick}
                                        className={classnames({
                                            [styles.link]: !isMobile,
                                            [styles.mobileLink]: isMobile,
                                        })}
                                    >
                                        <div className={styles.imgContainer}>
                                            {index < initialLoadedImages ? (
                                                imageElement
                                            ) : (
                                                <>
                                                    <LazyImage
                                                        className={styles.img}
                                                        src={imageUrl}
                                                        alt={designName || undefined}
                                                        offsetVertical={
                                                            isMobile
                                                                ? VERTICAL_OFFSET_MOBILE
                                                                : VERTICAL_OFFSET_DESKTOP
                                                        }
                                                        sizes="(max-width:768px) 100px, 200px"
                                                        defaultImageSize={defaultImageSize}
                                                    />
                                                    <noscript>{imageElement}</noscript>
                                                </>
                                            )}
                                        </div>
                                        <div className={styles.text}>
                                            <span className={styles.creator}>
                                                {creators
                                                    ?.map(creator => {
                                                        const { displayName = '' } = creator || {};
                                                        return displayName;
                                                    })
                                                    .join(', ')}
                                            </span>
                                            <span className={styles.design}>{designName}</span>
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </HeadingLevel>
    );
};

export const DesignsFeaturedItems = createFragmentContainer(DesignsFeaturedItemsComponent, {
    iconicDesignsDirectoryPage: graphql`
        fragment DesignsFeaturedItems_iconicDesignsDirectoryPage on IconicDesignsDirectoryPage {
            designPages {
                designName
                designUri
                imageUrl
                creators {
                    displayName
                }
            }
        }
    `,
});
